import React, { ReactNode, useState, useEffect } from 'react'
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import Switch from '../../base/Switch/Switch'
import Pill, { PillProps, PillVariant } from '../../base/Pill/Pill'
import { SxProps } from '@mui/material/styles'

const defaultStyles = {
  root: {
    border: '1px solid #CBD6E6',
    boxShadow: '0px 0px 8px rgba(54, 67, 86, 0.05)',
    borderRadius: '8px',
    '&.Mui-disabled': {
      backgroundColor: '#FFFFFF',
    },
  },
  summary: {
    pointerEvents: 'none',
    padding: '0px 32px',
  },
  title: {
    fontWeight: 325,
    fontSize: '24px',
    height: '40px',
    lineHeight: '40px',
    color: '#566983' /* Blue/600 */,
  },
  toggle: {
    marginLeft: 'auto',
    pointerEvents: 'auto',
  },
  details: {
    padding: '8px 32px 24px',
  },
  pill: {
    margin: '4px 0 0 12px',
  },
}

export interface FieldGroupProps {
  children: ReactNode
  disabled?: boolean
  expandable?: boolean
  expanded?: boolean
  handleToggle?: Function
  pill?: PillProps
  sx?: SxProps
  title: string
}

/**
 * @param {ReactNode} children - react elements to display in the accordion drawer
 * @param {boolean} expandable - {default: false} should display the toggle to allow user to expand the drawer if not expanded
 * @param {boolean} expanded - {default: true} sets the state of the accordion drawer as expanded or not
 * @param {Function} handleToggle - callback function when the drawer expand switch is toggled
 * @param {PillProps} pill - displays in the title bar after the title
 * @param {SxProps} sx - styling
 * @param {string} title - title text
 */
const FieldGroup = ({
  children,
  disabled,
  expandable,
  expanded,
  handleToggle,
  pill,
  sx,
  title,
}: FieldGroupProps) => {

  const styles = { ...defaultStyles, ...sx }

  const [isExpanded, setIsExpanded] = useState(
    expanded ?? true,
  )

  useEffect(() => {
    setIsExpanded(expanded ?? true)
  }, [expanded])

  return (
      <Accordion sx={styles.root} expanded={isExpanded} disabled={disabled}>
        <AccordionSummary sx={styles.summary}>
          {title && <Typography sx={styles.title}>{title}</Typography>}
          {pill && Object.keys(pill).length > 0 && <Pill {...pill} sx={styles.pill} />}
          {expandable && (
            <>
              <Switch
                sx={styles.toggle}
                checked={isExpanded}
                disabled={disabled}
                onChange={() => {
                  // in the future if the consumer wants to completely manage the state,
                  // overriding all local state, the a prop should be added to turn local
                  // state management but in a way that by default the local state is managed
                  setIsExpanded(!isExpanded)
                  handleToggle && handleToggle(isExpanded)
                }}
              />
            </>
          )}
        </AccordionSummary>
        {children && <AccordionDetails sx={styles.details}>{children}</AccordionDetails>}
      </Accordion>
  )
}

FieldGroup.defaultProps = {
  children: null,
  disabled: false,
  expandable: false,
  expanded: true,
  pill: {},
  sx: {},
  title: ''
}

export default FieldGroup
